import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import { ToastProvider } from './hooks/useToast';
import { BadgeNotificationProvider } from './hooks/useBadgeNotifications';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LandingRoute } from './components/auth/LandingRoute';
import { AppLayout } from './components/layout/AppLayout';
import { AdminRoute } from './components/admin/AdminRoute';
import { AdminLayout } from './components/admin/AdminLayout';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy load heavy components
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const PublicPlantView = React.lazy(() => import('./components/plants/PublicPlantView'));
const PublicPlantSourceView = React.lazy(
  () => import('./components/plantSources/PublicPlantSourceView')
);
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const PlantsPage = React.lazy(() => import('./pages/PlantsPage'));
const AddPlantPage = React.lazy(() => import('./pages/AddPlantPage'));
const PlantDetailPage = React.lazy(() => import('./pages/PlantDetailPage'));
const EditPlantPage = React.lazy(() => import('./pages/EditPlantPage'));
const SeedsPage = React.lazy(() => import('./pages/SeedsPage'));
const SeedDetailPage = React.lazy(() => import('./pages/SeedDetailPage'));
const PublicSeedView = React.lazy(() => import('./components/seeds/PublicSeedView'));
const LabelsPage = React.lazy(() => import('./pages/LabelsPage'));
const PlantSourcesPage = React.lazy(() => import('./pages/PlantSourcesPage'));
const UserBadgesPage = React.lazy(() => import('./pages/UserBadgesPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));

// Admin pages
const AdminBadgesPage = React.lazy(() => import('./pages/AdminBadgesPage'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const TaxonomyManagementPage = React.lazy(() => import('./pages/admin/TaxonomyManagementPage'));
const PlantVarietiesPage = React.lazy(() => import('./pages/admin/PlantVarietiesPage'));
const ScraperTrainingPage = React.lazy(() => import('./pages/admin/ScraperTrainingPage'));
const SiteHarvestPage = React.lazy(() => import('./pages/admin/SiteHarvestPage'));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Create a client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection time (was cacheTime in v4)
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        if (error && typeof error === 'object' && 'response' in error) {
          const httpError = error as { response?: { status?: number } };
          if (httpError.response?.status === 404) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="plantqr-theme">
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider>
              <BadgeNotificationProvider>
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      {/* Landing page */}
                      <Route
                        path="/"
                        element={
                          <LandingRoute>
                            <LandingPage />
                          </LandingRoute>
                        }
                      />

                      {/* Public routes */}
                      <Route
                        path="/login"
                        element={
                          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                            <LoginForm />
                          </div>
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                            <RegisterForm />
                          </div>
                        }
                      />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

                      {/* Public plant view - accessible via QR code */}
                      <Route path="/plant/:qrCodeId" element={<PublicPlantView />} />

                      {/* Public plant source view - accessible via QR code */}
                      <Route path="/source/:qrCodeId" element={<PublicPlantSourceView />} />

                      {/* Public seed view - accessible via QR code */}
                      <Route path="/seed/:qrCodeId" element={<PublicSeedView />} />

                      {/* Protected routes */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <DashboardPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/plants"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <PlantsPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/plants/add"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <AddPlantPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* IMPORTANT: Edit route must come before /plants/:id */}
                      <Route
                        path="/plants/:id/edit"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <EditPlantPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/plants/:id"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <PlantDetailPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/seeds"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <SeedsPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/seeds/:id"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <SeedDetailPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/badges"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <UserBadgesPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/labels"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <LabelsPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/sources"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <PlantSourcesPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/sources/add"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <PlantSourcesPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ProfilePage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin routes */}
                      <Route
                        path="/admin/*"
                        element={
                          <AdminRoute>
                            <AdminLayout />
                          </AdminRoute>
                        }
                      >
                        <Route path="badges" element={<AdminBadgesPage />} />
                        <Route path="users" element={<AdminUsersPage />} />
                        <Route path="taxonomy" element={<TaxonomyManagementPage />} />
                        <Route path="varieties" element={<PlantVarietiesPage />} />
                        <Route path="scraper" element={<ScraperTrainingPage />} />
                        <Route path="site-harvest" element={<SiteHarvestPage />} />
                      </Route>

                      {/* Catch all - redirect to landing page */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </BadgeNotificationProvider>
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
